// Library Methods
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import DefaultCard from "src/components/shared/layouts/DefaultCard/DefaultCard";
import Alert from "src/components/shared/alerts/Alert/Alert";
import ExamPackageSlider from "./ExamPackageSlider/ExamPackageSlider";
import ExamPackageDescription from "./ExamPackageDescription/ExamPackageDescription";
import SharedModal from "src/components/shared/modals/SharedModal/SharedModal";
import { PlusIcon } from "src/assets/icons";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";

// Hooks
import { useClinicPackages } from "src/services/clinics/hooks/clinic-packages/useClinicPackages";
import { useAddClinicPackage } from "src/services/clinics/hooks/clinic-packages/useAddClinicPackage";
import { useUpdateClinicPackage } from "src/services/clinics/hooks/clinic-packages/useUpdateClinicPackage";
import { useDeleteClinicPackage } from "src/services/clinics/hooks/clinic-packages/useDeleteClinicPackage";
import { useAddExams } from "src/hooks/exams/useAddExams";
import { useEditExams } from "src/hooks/exams/useEditExams";
import { useAddEditExamHelpers } from "src/hooks/exams/useAddEditExamHelpers";

// Styles
import {
  ModalCustomConfirmDarkCta,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";
import { UI_SIZES } from "src/constants/uiTokens";

// Types
import { ClinicPackageType } from "src/services/clinics/types";

type ExamPackageSettingsType = {
  isLoadingSettings: boolean;
  isUpdatingSettings: boolean;
};

const ExamPackageSettings = ({
  isLoadingSettings,
  isUpdatingSettings,
}: ExamPackageSettingsType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();

  const { selectedExamCards, actions } = useExamSliderContext();
  const { setSelectedExamCards } = actions;

  const { getFormattedExamSections: formatExamToAdd } = useAddExams();
  const { getFormattedExamSections: formatExamToEdit } = useEditExams();
  const { generateExamsToEdit } = useAddEditExamHelpers();

  // open / close modal and drawer values
  const [openAddDrawer, setOpenAddDrawer] = useState(false);
  const [openEditDrawer, setOpenEditDrawer] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [packageName, setPackageName] = useState("");
  const [selectedPackage, setSelectedPackage] = useState<
    ClinicPackageType | undefined
  >();

  const {
    data: packageData,
    isLoading: isLoadingPackages,
    isError: isErrorPackages,
    isFetching: isFetchingPackages,
    isRefetching: isRefetchingPackages,
  } = useClinicPackages(clinicInfo?.id);

  const { mutate: addPackageMutate, isPending: addPackagePending } =
    useAddClinicPackage(clinicInfo?.id);
  const { mutate: updatePackageMutate, isPending: updatePackagePending } =
    useUpdateClinicPackage(clinicInfo?.id);
  const { mutate: deletePackageMutate, isPending: deletePackagePending } =
    useDeleteClinicPackage(clinicInfo?.id);

  const isLoading = useMemo(
    () => isLoadingSettings || isLoadingPackages,
    [isLoadingSettings, isLoadingPackages]
  );

  const isUpdating = useMemo(
    () =>
      isUpdatingSettings ||
      isFetchingPackages ||
      isRefetchingPackages ||
      addPackagePending ||
      updatePackagePending ||
      deletePackagePending,
    [
      isUpdatingSettings,
      isFetchingPackages,
      isRefetchingPackages,
      addPackagePending,
      updatePackagePending,
      deletePackagePending,
    ]
  );

  const hasPackages = useMemo(
    () =>
      !isLoadingPackages &&
      !isErrorPackages &&
      packageData &&
      packageData?.length > 0,
    [isLoadingPackages, isErrorPackages, packageData]
  );

  const hasNoPackages = useMemo(
    () => !isLoadingPackages && !isErrorPackages && !packageData?.length,
    [isLoadingPackages, isErrorPackages, packageData]
  );

  const resetValues = useCallback(() => {
    setSelectedExamCards([]);
    setPackageName("");
    setSelectedPackage(undefined);
  }, [setSelectedExamCards]);

  const addExamPackage = useCallback(() => {
    const addData = {
      name: packageName,
      content: JSON.stringify({
        ...formatExamToAdd(selectedExamCards),
      }),
    };
    addPackageMutate(addData, {
      onSuccess: () => {
        setOpenAddDrawer(false);
        resetValues();
      },
    });
  }, [
    addPackageMutate,
    formatExamToAdd,
    resetValues,
    selectedExamCards,
    packageName,
  ]);

  const editExamPackage = useCallback(() => {
    if (!selectedPackage) return;

    const updateData = {
      packageId: selectedPackage?.id,
      packageData: {
        name: packageName,
        content: JSON.stringify({
          ...formatExamToEdit(selectedExamCards),
        }),
      },
    };
    updatePackageMutate(updateData, {
      onSuccess: () => {
        setOpenEditDrawer(false);
        resetValues();
      },
    });
  }, [
    updatePackageMutate,
    formatExamToEdit,
    resetValues,
    selectedPackage,
    selectedExamCards,
    packageName,
  ]);

  const deleteExamPackage = useCallback(() => {
    if (!selectedPackage) return;

    deletePackageMutate(selectedPackage?.id, {
      onSuccess: () => {
        setOpenDeleteModal(false);
        resetValues();
      },
    });
  }, [deletePackageMutate, resetValues, selectedPackage]);

  return (
    <>
      <Typography
        sx={{ textTransform: "uppercase" }}
        fontSize="16px"
        color={mutedText}
      >
        {t("Exam Package Settings")}
      </Typography>

      <DefaultCard>
        <Box display="flex" flexDirection="column" gap="16px">
          <Box display="flex" justifyContent="space-between" gap="16px">
            <Box display="flex" flexDirection="column" gap="6px">
              <Typography fontSize="14px" fontWeight={600}>
                {t("Clinic Exam Packages")}
              </Typography>
              <Typography fontSize="14px" color={mutedText}>
                {t(
                  "Create up to 3 preset exam packages that can be quickly applied to any patient."
                )}
              </Typography>
            </Box>
            {packageData && packageData?.length < 3 && (
              <Button
                disabled={isLoading || isUpdating}
                sx={ModalCustomConfirmDarkCta}
                startIcon={<PlusIcon />}
                onClick={() => setOpenAddDrawer(true)}
              >
                {t("Add")}
              </Button>
            )}
          </Box>
          {isLoading ? (
            <Box display="flex" flexDirection="column" gap="16px">
              <Skeleton
                variant="rounded"
                width="100%"
                height={UI_SIZES.inputHeight}
                sx={{
                  borderRadius: "4px",
                }}
              />
              <Skeleton
                variant="rounded"
                width="100%"
                height={90}
                sx={{
                  borderRadius: "4px",
                }}
              />
            </Box>
          ) : (
            <Box display="flex" flexWrap="wrap" gap="12px">
              {hasNoPackages && (
                <Alert
                  variant="warning"
                  alertContent={t("No exam packages found.")}
                  props={{ width: "100%" }}
                />
              )}
              {hasPackages &&
                packageData
                  ?.sort((a, b) => a?.name?.localeCompare(b?.name))
                  ?.map((clinicPackage) => (
                    <Box
                      key={clinicPackage?.id}
                      sx={{
                        flexGrow: 1,
                        flexBasis: {
                          xs: "100%",
                          sm: "calc(50% - 8px)",
                          lg: "calc(33.33% - 11px)",
                        },
                        "& > *": { height: "100%" },
                      }}
                    >
                      <ExamPackageDescription
                        isUpdating={isUpdating}
                        clinicPackage={clinicPackage}
                        openDeleteModal={() => {
                          setSelectedPackage(clinicPackage);
                          setOpenDeleteModal(true);
                        }}
                        openEditDrawer={() => {
                          const examContent = JSON.parse(
                            clinicPackage?.content
                          );
                          const selectedExamCards =
                            generateExamsToEdit(examContent);

                          setSelectedExamCards([...selectedExamCards]);
                          setPackageName(clinicPackage.name);
                          setSelectedPackage(clinicPackage);
                          setOpenEditDrawer(true);
                        }}
                      />
                    </Box>
                  ))}
            </Box>
          )}
        </Box>
      </DefaultCard>
      <SharedModal
        open={openDeleteModal}
        title={t("Delete Exam Package?")}
        modalDescription={t(
          "Are you sure you want to delete '{{ packageName }}'?",
          { packageName: selectedPackage?.name }
        )}
        confirmButtonText={t("Delete")}
        confirmButtonVariant="error"
        onClose={() => {
          setOpenDeleteModal(false);
          resetValues();
        }}
        onConfirm={deleteExamPackage}
      />
      {/* Add package slider */}
      <ExamPackageSlider
        key="add-exam-package"
        title={t("Add Exam Package")}
        isOpen={openAddDrawer}
        isLoading={isUpdating}
        onClose={() => {
          setOpenAddDrawer(false);
          resetValues();
        }}
        onConfirm={addExamPackage}
        packageName={packageName}
        setPackageName={setPackageName}
      />
      {/* Edit package slider */}
      <ExamPackageSlider
        key="edit-exam-package"
        title={t("Edit Exam Package")}
        isOpen={openEditDrawer}
        isLoading={isUpdating}
        onClose={() => {
          setOpenEditDrawer(false);
          resetValues();
        }}
        onConfirm={editExamPackage}
        packageName={packageName}
        setPackageName={setPackageName}
      />
    </>
  );
};

export default ExamPackageSettings;
